import { useEffect, useState, useCallback } from "react";
import { localToday } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Wand2, ShieldAlert } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import CascadeDeleteDialog from "@/components/CascadeDeleteDialog";
import BulkOpsDialog from "@/components/BulkOpsDialog";
import { Checkbox } from "@/components/ui/checkbox";

export default function MedewerkersPage() {
  const { user } = useAuth();
  const canWrite = can(user, "staff", "write");
  const canDelete = can(user, "staff", "delete");
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());
  const [editingId, setEditingId] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null);

  const load = useCallback(async () => {
    const [m, l, g] = await Promise.all([api.get("/medewerkers"), api.get("/locations"), api.get("/groups")]);
    setRows(m.data); setLocations(l.data); setGroups(g.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form, contract_uren: parseFloat(form.contract_uren) };
      if (editingId) {
        await api.put(`/medewerkers/${editingId}`, payload);
        toast.success("Medewerker bijgewerkt");
      } else {
        await api.post("/medewerkers", payload);
        toast.success("Medewerker toegevoegd");
      }
      setOpen(false); setForm(init()); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (row) => {
    setForm({
      voornaam: row.voornaam || "", achternaam: row.achternaam || "",
      email: row.email || "", telefoon: row.telefoon || "",
      functie: row.functie || "Pedagogisch medewerker",
      contract_uren: row.contract_uren ?? 32,
      location_ids: row.location_ids || [], group_ids: row.group_ids || [],
      in_dienst_sinds: row.in_dienst_sinds || localToday(),
      gediplomeerd: row.gediplomeerd !== false,
      diploma_type: row.diploma_type || "PW3",
      beschikbaarheid: row.beschikbaarheid || init().beschikbaarheid,
      voorkeur_opener: !!row.voorkeur_opener,
      voorkeur_sluiter: !!row.voorkeur_sluiter,
      heeft_ehbo: !!row.heeft_ehbo,
      ehbo_geldig_tot: row.ehbo_geldig_tot || "",
      heeft_babytraining: !!row.heeft_babytraining,
      babytraining_behaald_op: row.babytraining_behaald_op || "",
    });
    setEditingId(row.id);
    setOpen(true);
  };

  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));
  const grpMap = Object.fromEntries(groups.map(g => [g.id, g.name]));

  const columns = [
    { key: "achternaam", label: "Naam", render: r => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[color:var(--terracotta)] text-white font-semibold text-xs">
          {r.voornaam?.[0]}{r.achternaam?.[0]}
        </div>
        <div>
          <div className="font-medium text-neutral-800">{r.voornaam} {r.achternaam}</div>
          <div className="text-xs text-neutral-500">{r.email}</div>
        </div>
      </div>
    )},
    { key: "functie", label: "Functie" },
    { key: "contract_uren", label: "Contract", render: r => <span className="font-mono">{r.contract_uren} uur</span> },
    { key: "location_ids", label: "Locatie(s)", sortable: false, render: r => (
      <div className="flex flex-wrap gap-1">
        {(r.location_ids || []).map(id => <Badge key={id} variant="outline" className="border-neutral-200 text-neutral-700">{locMap[id]}</Badge>)}
      </div>
    )},
    { key: "group_ids", label: "Groep(en)", sortable: false, render: r => (
      <div className="flex flex-wrap gap-1">
        {(r.group_ids || []).map(id => <Badge key={id} variant="outline" className="bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent">{grpMap[id]}</Badge>)}
      </div>
    )},
    { key: "active", label: "Actief", render: r => r.active
      ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" variant="outline">Actief</Badge>
      : <Badge className="bg-neutral-100 text-neutral-500 border-neutral-200 border" variant="outline">Inactief</Badge> },
    { key: "beschikbaarheid", label: "Beschikbaarheid", sortable: false, render: r => {
      const b = r.beschikbaarheid || {};
      const days = [
        ["maandag", "Ma"], ["dinsdag", "Di"], ["woensdag", "Wo"],
        ["donderdag", "Do"], ["vrijdag", "Vr"],
      ];
      const filled = days.filter(([k]) => b[k]?.start && b[k]?.eind);
      if (filled.length === 0) {
        return <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px]" variant="outline" data-testid={`avail-empty-${r.id}`}>Niet ingevuld</Badge>;
      }
      const complete = filled.length === 5;
      return (
        <div className="flex flex-wrap gap-0.5" data-testid={`avail-${r.id}`} title={filled.map(([k, l]) => `${l}: ${b[k].start}-${b[k].eind}`).join("\n")}>
          {days.map(([k, l]) => {
            const has = b[k]?.start && b[k]?.eind;
            return (
              <span key={k}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${has ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-neutral-100 text-neutral-400 border-neutral-200 line-through"}`}>
                {l}
              </span>
            );
          })}
          {complete && <span className="text-[9px] text-emerald-600 ml-1">✓ volledig</span>}
        </div>
      );
    }},
    { key: "diploma", label: "Diploma", sortable: false, render: r => r.gediplomeerd === false
      ? <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200 border" variant="outline">Nee</Badge>
      : <Badge className="bg-blue-50 text-blue-700 border-blue-200 border" variant="outline">{r.diploma_type || "Ja"}</Badge> },
    { key: "certificaten", label: "Certificaten", sortable: false, render: r => {
      const tone = {
        ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
        binnenkort: "bg-amber-50 text-amber-800 border-amber-300",
        verlopen: "bg-red-50 text-red-700 border-red-200",
        geen: "bg-neutral-100 text-neutral-500 border-neutral-200",
      };
      const ehboLabel = {
        ok: "EHBO ✓",
        binnenkort: "EHBO ⚠ 30d",
        verlopen: "EHBO verlopen",
        geen: null,
      }[r.ehbo_status || "geen"];
      return (
        <div className="flex gap-1 flex-wrap">
          {ehboLabel && (
            <Badge className={`text-[10px] border ${tone[r.ehbo_status]}`} variant="outline"
              title={r.ehbo_geldig_tot ? `Geldig tot ${r.ehbo_geldig_tot}` : ""}
              data-testid={`ehbo-badge-${r.id}`}>
              {ehboLabel}
            </Badge>
          )}
          {r.heeft_babytraining && (
            <Badge className="text-[10px] bg-pink-50 text-pink-700 border-pink-200 border" variant="outline">Baby</Badge>
          )}
        </div>
      );
    }},
  ];
  if (canWrite) {
    columns.push({
      key: "bewerken", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(r); }}
          data-testid={`edit-medewerker-${r.id}`}
          className="text-[color:var(--sage)] hover:bg-[color:var(--sage)]/10" title="Bewerk">
          <Pencil className="h-4 w-4" />
        </Button>
      ),
    });
  }
  if (canDelete) {
    columns.push({
      key: "verwijder", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteFor(r); }}
          data-testid={`delete-medewerker-${r.id}`}
          className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Verwijder">
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    });
  }

  return (
    <div>
      <PageHeader
        title="Medewerkers"
        subtitle="Pedagogisch medewerkers en team. Locatie en groep bepalen zichtbaarheid voor RBAC."
        testid="medewerkers-header"
        actions={canWrite && (
          <div className="flex items-center gap-2 flex-wrap">
            <ComplianceQuickEditButton rows={rows} onDone={load} />
            <BulkAvailabilityButton rows={rows} onDone={load} />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(init()); } }}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-medewerker-btn">
                <Plus className="h-4 w-4 mr-1" /> Nieuwe medewerker
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Medewerker bewerken" : "Nieuwe medewerker"}</DialogTitle>
                <DialogDescription>Vul basisgegevens + diploma + beschikbaarheid in — cruciaal voor AI-rooster-suggesties.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Voornaam</Label><Input value={form.voornaam} onChange={e => setForm({ ...form, voornaam: e.target.value })} /></div>
                <div><Label>Achternaam</Label><Input value={form.achternaam} onChange={e => setForm({ ...form, achternaam: e.target.value })} /></div>
                <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefoon</Label><Input value={form.telefoon} onChange={e => setForm({ ...form, telefoon: e.target.value })} /></div>
                <div><Label>Functie</Label><Input value={form.functie} onChange={e => setForm({ ...form, functie: e.target.value })} /></div>
                <div><Label>Contract-uren</Label><Input type="number" value={form.contract_uren} onChange={e => setForm({ ...form, contract_uren: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Locatie(s) — meerdere mogelijk</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1" data-testid="medewerker-locations-multi">
                    {locations.map(l => {
                      const on = form.location_ids.includes(l.id);
                      return (
                        <button key={l.id} type="button"
                          onClick={() => {
                            const next = on
                              ? form.location_ids.filter(x => x !== l.id)
                              : [...form.location_ids, l.id];
                            // Bij deselect: pull ook de bijbehorende groepen
                            const groupsToKeep = form.group_ids.filter(gid => {
                              const g = groups.find(gg => gg.id === gid);
                              return g && next.includes(g.location_id);
                            });
                            setForm({ ...form, location_ids: next, group_ids: groupsToKeep });
                          }}
                          className={`text-sm px-3 py-2 rounded-md border transition-colors text-left ${on ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 hover:bg-neutral-100"}`}
                          data-testid={`medewerker-loc-toggle-${l.id}`}
                        >
                          {on && <span className="mr-1">✓</span>}{l.name}
                        </button>
                      );
                    })}
                  </div>
                  {form.location_ids.length > 1 && (
                    <p className="text-xs text-neutral-500 mt-1">
                      Deze medewerker werkt op {form.location_ids.length} locaties.
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Groep(en)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {groups.filter(g => form.location_ids.includes(g.location_id)).map(g => {
                      const on = form.group_ids.includes(g.id);
                      return (
                        <button key={g.id} type="button"
                          onClick={() => setForm({ ...form, group_ids: on ? form.group_ids.filter(x => x !== g.id) : [...form.group_ids, g.id] })}
                          className={`text-sm px-3 py-2 rounded-md border transition-colors ${on ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 hover:bg-neutral-100"}`}
                        >{g.name}</button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Diploma & Voorkeuren */}
              <div className="pt-4 mt-4 border-t border-neutral-100">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Diploma, certificaten & voorkeuren</div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <label className="flex items-center gap-2 text-sm col-span-1">
                    <Checkbox checked={form.gediplomeerd} onCheckedChange={c => setForm({ ...form, gediplomeerd: !!c })} data-testid="med-gediplomeerd" /> Gediplomeerd
                  </label>
                  <div className="col-span-2">
                    <Label>Diploma-type</Label>
                    <Select value={form.diploma_type || "PW3"} onValueChange={v => setForm({ ...form, diploma_type: v })}>
                      <SelectTrigger data-testid="med-diploma-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {["PW3", "PW4", "SPW3", "SPW4", "HBO Pedagogiek", "Anders"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.heeft_ehbo} onCheckedChange={c => setForm({ ...form, heeft_ehbo: !!c })} data-testid="med-ehbo" /> Kinder-EHBO
                  </label>
                  <div className="col-span-2">
                    <Label>EHBO-certificaat geldig tot</Label>
                    <Input type="date" value={form.ehbo_geldig_tot || ""} disabled={!form.heeft_ehbo}
                      onChange={e => setForm({ ...form, ehbo_geldig_tot: e.target.value })} data-testid="med-ehbo-tot" />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.heeft_babytraining} onCheckedChange={c => setForm({ ...form, heeft_babytraining: !!c })} data-testid="med-babytraining" /> Babytraining
                  </label>
                  <div className="col-span-2">
                    <Label>Babytraining behaald op</Label>
                    <Input type="date" value={form.babytraining_behaald_op || ""} disabled={!form.heeft_babytraining}
                      onChange={e => setForm({ ...form, babytraining_behaald_op: e.target.value })} data-testid="med-baby-datum" />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.voorkeur_opener} onCheckedChange={c => setForm({ ...form, voorkeur_opener: !!c })} data-testid="med-vk-opener" /> Voorkeur opener
                  </label>
                  <label className="flex items-center gap-2 text-sm col-span-2">
                    <Checkbox checked={form.voorkeur_sluiter} onCheckedChange={c => setForm({ ...form, voorkeur_sluiter: !!c })} data-testid="med-vk-sluiter" /> Voorkeur sluiter
                  </label>
                </div>
              </div>

              {/* Beschikbaarheid per weekdag */}
              <div className="pt-4 mt-4 border-t border-neutral-100">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Beschikbaarheid per weekdag</div>
                <div className="grid grid-cols-1 gap-2">
                  {["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag"].map(day => {
                    const b = form.beschikbaarheid?.[day] || {};
                    const enabled = !!b.start;
                    return (
                      <div key={day} className="grid grid-cols-6 gap-2 items-center">
                        <label className="flex items-center gap-2 text-sm capitalize col-span-2">
                          <Checkbox
                            checked={enabled}
                            data-testid={`med-day-${day}`}
                            onCheckedChange={c => setForm({
                              ...form,
                              beschikbaarheid: {
                                ...form.beschikbaarheid,
                                [day]: c ? { start: "08:00", eind: "17:00" } : {},
                              }
                            })}
                          /> {day}
                        </label>
                        <Input type="time" disabled={!enabled} value={b.start || ""} className="col-span-2"
                          onChange={e => setForm({ ...form, beschikbaarheid: { ...form.beschikbaarheid, [day]: { ...b, start: e.target.value } } })}
                        />
                        <Input type="time" disabled={!enabled} value={b.eind || ""} className="col-span-2"
                          onChange={e => setForm({ ...form, beschikbaarheid: { ...form.beschikbaarheid, [day]: { ...b, eind: e.target.value } } })}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="text-[11px] text-neutral-500 mt-2">Zonder ingestelde beschikbaarheid rekent AI-rooster met een default van 08:00–17:00 op werkdagen.</div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); setForm(init()); }}>Annuleren</Button>
                <Button onClick={submit} data-testid="med-save-btn" style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 p-2.5 bg-[color:var(--sage-100)]/40 border border-[color:var(--sage-100)] rounded-md" data-testid="medewerkers-bulk-toolbar">
          <span className="text-sm font-medium text-[color:var(--sage)]" data-testid="medewerkers-bulk-count">{selected.size} geselecteerd</span>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="medewerkers-bulk-clear">Selectie wissen</Button>
          <div className="flex-1" />
          {canWrite && (
            <Button size="sm" onClick={() => setBulkMode("update")} data-testid="medewerkers-bulk-update"
                    style={{ background: "var(--sage)" }} className="text-white">
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Wijzig {selected.size}
            </Button>
          )}
          {(user?.role === "superadmin") && (
            <Button size="sm" onClick={() => setBulkMode("delete")} data-testid="medewerkers-bulk-delete"
                    className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Verwijder {selected.size}
            </Button>
          )}
        </div>
      )}
      <DataTable columns={columns} rows={rows} testid="medewerkers-table"
                 selectable={canWrite} selected={selected} onSelectionChange={setSelected} />
      <CascadeDeleteDialog
        open={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        resource="medewerkers"
        resourceId={deleteFor?.id}
        resourceLabel={deleteFor ? `${deleteFor.voornaam} ${deleteFor.achternaam}` : ""}
        onDeleted={load}
      />
      <BulkOpsDialog
        open={!!bulkMode}
        onClose={() => setBulkMode(null)}
        mode={bulkMode}
        resource="medewerkers"
        ids={[...selected]}
        onDone={() => { setSelected(new Set()); load(); }}
        updateFields={[
          { key: "location_ids", label: "Locaties (vervangt bestaande)", type: "multi",
            options: locations.map(l => ({ value: l.id, label: l.name })) },
          { key: "group_ids", label: "Groepen (vervangt bestaande)", type: "multi",
            options: groups.map(g => ({ value: g.id, label: g.name })) },
          { key: "active", label: "Status", type: "boolean",
            trueLabel: "Actief", falseLabel: "Uit dienst" },
          { key: "functie", label: "Functie", type: "text",
            placeholder: "Bijv. Pedagogisch medewerker" },
        ]}
      />
    </div>
  );
}

function init() {
  return {
    voornaam: "", achternaam: "", email: "", telefoon: "",
    functie: "Pedagogisch medewerker",
    contract_uren: 32, location_ids: [], group_ids: [],
    in_dienst_sinds: localToday(),
    gediplomeerd: true,
    diploma_type: "PW3",
    beschikbaarheid: {
      maandag: { start: "08:00", eind: "17:00" },
      dinsdag: { start: "08:00", eind: "17:00" },
      woensdag: { start: "08:00", eind: "17:00" },
      donderdag: { start: "08:00", eind: "17:00" },
      vrijdag: { start: "08:00", eind: "17:00" },
    },
    voorkeur_opener: false,
    voorkeur_sluiter: false,
    heeft_ehbo: false,
    ehbo_geldig_tot: "",
    heeft_babytraining: false,
    babytraining_behaald_op: "",
  };
}


/** Snelle bulk-editor voor EHBO/babytraining vervaldata — één tabel, alle medewerkers,
 * inline datum-invoer. Handig direct na een team-audit om alle certificaten in te voeren. */
function ComplianceQuickEditButton({ rows, onDone }) {
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const initFromRows = () => {
    const map = {};
    for (const r of rows) {
      map[r.id] = {
        heeft_ehbo: !!r.heeft_ehbo,
        ehbo_geldig_tot: r.ehbo_geldig_tot || "",
        heeft_babytraining: !!r.heeft_babytraining,
        babytraining_behaald_op: r.babytraining_behaald_op || "",
      };
    }
    setEdits(map);
  };

  const set = (id, k, v) => setEdits(e => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const saveAll = async () => {
    setBusy(true);
    try {
      const changed = [];
      for (const r of rows) {
        const cur = edits[r.id];
        if (!cur) continue;
        // Alleen posten als er echt iets veranderd is t.o.v. de oorspronkelijke row
        const changed_ehbo = !!cur.heeft_ehbo !== !!r.heeft_ehbo || (cur.ehbo_geldig_tot || "") !== (r.ehbo_geldig_tot || "");
        const changed_baby = !!cur.heeft_babytraining !== !!r.heeft_babytraining || (cur.babytraining_behaald_op || "") !== (r.babytraining_behaald_op || "");
        if (!changed_ehbo && !changed_baby) continue;
        changed.push(r.id);
        // Belangrijk: PUT /medewerkers/{id} vervangt ALLE velden via MedewerkerCreate,
        // dus we starten met de HELE row om beschikbaarheid/in_dienst_sinds/voorkeur_*
        // niet stil te overschrijven met defaults. We overriden alleen de 4
        // compliance-velden uit de quick-edit.
        const payload = {
          ...r,
          heeft_ehbo: !!cur.heeft_ehbo,
          ehbo_geldig_tot: cur.heeft_ehbo ? (cur.ehbo_geldig_tot || null) : null,
          heeft_babytraining: !!cur.heeft_babytraining,
          babytraining_behaald_op: cur.heeft_babytraining ? (cur.babytraining_behaald_op || null) : null,
        };
        // Backend accepteert geen `id`, `created_at`, `updated_at` in de body.
        delete payload.id;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload._id;
        await api.put(`/medewerkers/${r.id}`, payload);
      }
      toast.success(`${changed.length} medewerker(s) bijgewerkt`);
      onDone?.();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { initFromRows(); setOpen(true); }} data-testid="compliance-quick-edit-btn">
        <ShieldAlert className="h-4 w-4 mr-1" /> Compliance snel invullen
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="compliance-quick-dialog">
          <DialogHeader>
            <DialogTitle>Compliance-data snel invullen</DialogTitle>
            <DialogDescription>
              Vul EHBO-vervaldata en babytraining voor alle medewerkers in één overzicht.
              Vervallen certificaten worden op het Dashboard direct als 'kritiek' getoond.
            </DialogDescription>
          </DialogHeader>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="text-left py-2">Naam</th>
                <th className="text-center py-2">EHBO</th>
                <th className="text-left py-2">Geldig tot</th>
                <th className="text-center py-2">Baby</th>
                <th className="text-left py-2">Behaald op</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cur = edits[r.id] || {};
                return (
                  <tr key={r.id} className="border-b border-neutral-100" data-testid={`cq-row-${r.id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-neutral-800">{r.voornaam} {r.achternaam}</div>
                      <div className="text-[10px] text-neutral-500">{r.functie || "PM"}</div>
                    </td>
                    <td className="py-2 text-center">
                      <input type="checkbox" checked={!!cur.heeft_ehbo}
                        onChange={e => set(r.id, "heeft_ehbo", e.target.checked)}
                        data-testid={`cq-ehbo-${r.id}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <Input type="date" value={cur.ehbo_geldig_tot || ""}
                        disabled={!cur.heeft_ehbo}
                        onChange={e => set(r.id, "ehbo_geldig_tot", e.target.value)}
                        className="h-8 text-xs"
                        data-testid={`cq-ehbo-date-${r.id}`} />
                    </td>
                    <td className="py-2 text-center">
                      <input type="checkbox" checked={!!cur.heeft_babytraining}
                        onChange={e => set(r.id, "heeft_babytraining", e.target.checked)}
                        data-testid={`cq-baby-${r.id}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <Input type="date" value={cur.babytraining_behaald_op || ""}
                        disabled={!cur.heeft_babytraining}
                        onChange={e => set(r.id, "babytraining_behaald_op", e.target.value)}
                        className="h-8 text-xs"
                        data-testid={`cq-baby-date-${r.id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={saveAll} disabled={busy} data-testid="cq-save" style={{ background: "var(--sage)" }} className="text-white">
              {busy ? "Bezig…" : "Alles opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



/** BulkAvailabilityButton — dialog om beschikbaarheid voor meerdere medewerkers
 * tegelijk in te vullen. Selecteer medewerkers → vink weekdagen aan met tijden →
 * kies overwrite/merge → opslaan. Voorkomt dat directeur 14× dezelfde velden vult. */
function BulkAvailabilityButton({ rows, onDone }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [besch, setBesch] = useState({});
  const [mode, setMode] = useState("overwrite");
  const [saving, setSaving] = useState(false);

  const toggleMed = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const selectAll = () => setSelected(new Set(rows.filter(r => r.active !== false).map(r => r.id)));
  const selectNone = () => setSelected(new Set());

  const applyPreset = (preset) => {
    if (preset === "workweek") {
      setBesch({
        maandag: { start: "08:00", eind: "17:00" }, dinsdag: { start: "08:00", eind: "17:00" },
        woensdag: { start: "08:00", eind: "17:00" }, donderdag: { start: "08:00", eind: "17:00" },
        vrijdag: { start: "08:00", eind: "17:00" },
      });
    } else if (preset === "clear") { setBesch({}); }
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Selecteer minstens 1 medewerker"); return; }
    if (Object.keys(besch).length === 0) { toast.error("Vink minstens 1 weekdag aan"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/medewerkers/bulk-beschikbaarheid", {
        medewerker_ids: Array.from(selected), beschikbaarheid: besch, mode,
      });
      toast.success(`Beschikbaarheid bijgewerkt voor ${data.updated} medewerker(s)`);
      setOpen(false); setSelected(new Set()); setBesch({}); onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update mislukt");
    } finally { setSaving(false); }
  };

  const days = [["maandag","Ma"],["dinsdag","Di"],["woensdag","Wo"],["donderdag","Do"],["vrijdag","Vr"]];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-availability-btn">
          <Wand2 className="h-4 w-4 mr-1" /> Bulk beschikbaarheid
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle>Beschikbaarheid voor meerdere medewerkers</DialogTitle>
          <DialogDescription>
            Selecteer medewerkers en vul de weekdagen tegelijk in — voorkomt 14 losse edits.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Medewerkers ({selected.size} geselecteerd)</Label>
              <div className="flex gap-1">
                <button className="text-[11px] text-[color:var(--sage)] hover:underline" onClick={selectAll} data-testid="bulk-avail-selectall">Alle actief</button>
                <span className="text-[11px] text-neutral-400">·</span>
                <button className="text-[11px] text-neutral-600 hover:underline" onClick={selectNone}>Geen</button>
              </div>
            </div>
            <div className="border rounded max-h-64 overflow-y-auto">
              {rows.map(r => (
                <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-0 text-sm cursor-pointer" data-testid={`bulk-avail-med-${r.id}`}>
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleMed(r.id)} />
                  <span className={r.active === false ? "text-neutral-400" : ""}>
                    {r.voornaam} {r.achternaam}
                    {r.active === false && <span className="text-[10px] text-neutral-400 ml-1">(inactief)</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Weekdagen</Label>
              <div className="flex gap-1">
                <button className="text-[11px] text-[color:var(--sage)] hover:underline" onClick={() => applyPreset("workweek")} data-testid="bulk-avail-preset-workweek">Ma-Vr 8-17</button>
                <span className="text-[11px] text-neutral-400">·</span>
                <button className="text-[11px] text-neutral-600 hover:underline" onClick={() => applyPreset("clear")}>Leeg</button>
              </div>
            </div>
            <div className="border rounded p-2 space-y-2">
              {days.map(([k, l]) => {
                const b = besch[k] || {};
                const enabled = !!b.start;
                return (
                  <div key={k} className="grid grid-cols-6 gap-1 items-center">
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={enabled}
                        onChange={e => setBesch({ ...besch, [k]: e.target.checked ? { start: "08:00", eind: "17:00" } : undefined })}
                        data-testid={`bulk-avail-day-${k}`} /> {l}
                    </label>
                    <Input type="time" disabled={!enabled} value={b.start || ""} className="col-span-2 h-8"
                      onChange={e => setBesch({ ...besch, [k]: { ...b, start: e.target.value } })} />
                    <Input type="time" disabled={!enabled} value={b.eind || ""} className="col-span-2 h-8"
                      onChange={e => setBesch({ ...besch, [k]: { ...b, eind: e.target.value } })} />
                  </div>
                );
              })}
            </div>
            <div className="mt-2">
              <Label className="text-[11px]">Modus</Label>
              <div className="flex gap-3 mt-1">
                <label className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="radio" checked={mode === "overwrite"} onChange={() => setMode("overwrite")} data-testid="bulk-avail-mode-overwrite" />
                  Overschrijven
                </label>
                <label className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="bulk-avail-mode-merge" />
                  Samenvoegen (bestaande dagen behouden)
                </label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={saving} data-testid="bulk-avail-save"
                  style={{ background: "var(--sage)" }} className="text-white">
            {saving ? "Opslaan…" : `Toepassen op ${selected.size} medewerker(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

